import React, { useState, useEffect, useRef } from 'react';
import { activitiesAPI } from '../../services/api';
import { parseCoordinates, formatLabel } from '../../utils/coordinateParser';
import { Label } from '../Common/Label';
import { Input } from '../Common/Input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../Common/Select';
import { Checkbox } from '../Common/Checkbox';
import { Slider } from '../Common/Slider';
import { Button } from '../Common/Button';
import FieldInfo from '../Common/FieldInfo';
import info from './activityFormInfoTexts';
import EventScheduleManager from './EventScheduleManager';

const categories = [
  'Sport & Bewegung',
  'Kultur & Kunst',
  'Natur & Tiere',
  'Forschung & Experiment',
  'Museen & Ausstellungen',
  'Spielplätze',
  'Schwimmbäder',
  'Kino',
  'Workshops'
];

const composeAddress = ({ locationStreet = '', locationPlz = '', locationCity = '' }) => {
  const street = (locationStreet || '').trim();
  const plz = (locationPlz || '').trim();
  const city = (locationCity || '').trim();
  const plzCity = [plz, city].filter(Boolean).join(' ');

  if (street && plzCity) return `${street}, ${plzCity}`;
  if (street) return street;
  return plzCity;
};

// Event-Typen, deren Gültigkeitszeitraum über date_ranges (Editor "Gültig ab/bis")
// gepflegt wird.
const TYPES_WITH_DATE_RANGES = ['regular_hours', 'recurring_event', 'freely_accessible'];

// Normalisiert das event_schedule für die Bearbeitung: Legacy-Gültigkeitsfelder
// werden in date_ranges überführt, damit die Datumsbereich-Editoren sie anzeigen –
// und beim Speichern nicht verloren gehen (regular_hours persistiert nur date_ranges).
//
// Präzedenz wie im Backend: date_ranges > event_schedule.valid_from/valid_until
// (inkl. camelCase) > accessible_from/accessible_until. Auf accessible_* wird nur
// zugegriffen, wenn das event_schedule keine eigene Gültigkeit definiert.
const withDerivedDateRanges = (activity) => {
  const schedule = { ...(activity?.event_schedule ?? {}) };
  const eventType = activity?.event_type ?? 'regular_hours';
  if (!TYPES_WITH_DATE_RANGES.includes(eventType)) return schedule;

  const existing = Array.isArray(schedule.date_ranges) ? schedule.date_ranges : [];
  if (existing.some((r) => r && (r.from || r.until))) return schedule;

  const scheduleFrom = schedule.valid_from ?? schedule.validFrom ?? null;
  const scheduleUntil = schedule.valid_until ?? schedule.validUntil ?? null;

  let from;
  let until;
  if (scheduleFrom || scheduleUntil) {
    // event_schedule hat Vorrang und wird als Einheit übernommen
    from = scheduleFrom || '';
    until = scheduleUntil || '';
  } else {
    from = activity?.accessible_from || '';
    until = activity?.accessible_until || '';
  }

  if (from || until) {
    schedule.date_ranges = [{ from, until }];
  }
  return schedule;
};

const mapActivityToFormData = (activity) => ({
  name: activity?.name ?? '',
  description: activity?.description ?? '',
  locationName: activity?.location_name ?? '',
  locationStreet: activity?.location_street ?? '',
  locationPlz: activity?.location_plz ?? '',
  locationCity: activity?.location_city ?? '',
  locationAddress: activity?.address ?? '',
  latitude: activity?.latitude != null ? String(activity.latitude) : '',
  longitude: activity?.longitude != null ? String(activity.longitude) : '',
  coordinateString: (activity?.latitude != null && activity?.longitude != null)
    ? `${activity.latitude}, ${activity.longitude}`
    : '',
  website: activity?.website ?? '',
  locationWebsite: activity?.location_website ?? '',
  categories: activity?.categories ?? [],
  ageMin: activity?.age_min ?? 0,
  ageMax: activity?.age_max ?? 12,
  weatherType: activity?.indoor_outdoor ?? '',
  cost: activity?.cost_range ?? '',
  duration: activity?.duration ?? '',
  wheelchairAccessible: !!activity?.wheelchair_accessible,
  eventType: activity?.event_type ?? 'regular_hours',
  openingHours: activity?.opening_hours ?? [],
  eventSchedule: withDerivedDateRanges(activity),
  googlePlaceId: activity?.google_place_id ?? '',
  isSeasonal: !!activity?.is_seasonal,
});

const ActivityForm = ({ onSuccess, mode = 'create', initialData, activityId, onCancel }) => {
  const isEditMode = mode === 'edit';
  const [formData, setFormData] = useState(() => mapActivityToFormData(initialData));
  const coordinateInput = formData?.coordinateString ?? '';
  const [locationInputMode, setLocationInputMode] = useState(() => {
    const initial = mapActivityToFormData(initialData);
    const hasCoords = initial.latitude !== '' && initial.longitude !== '';
    const hasStructuredAddress = Boolean(
      (initial.locationStreet || '').trim() ||
      (initial.locationPlz || '').trim() ||
      (initial.locationCity || '').trim()
    );
    const hasLegacyAddress = (initial.locationAddress || '').trim() !== '' && !initial.locationAddress.startsWith('Koordinaten:');
    const hasRealAddress = hasStructuredAddress || hasLegacyAddress;
    return hasCoords && !hasRealAddress ? 'coordinates' : 'address';
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const isResettingFormRef = useRef(false);
  const formRef = useRef(null);
  const eventScheduleSectionRef = useRef(null);
  const categoriesSectionRef = useRef(null);

  const scrollToRef = (ref) => {
    requestAnimationFrame(() => {
      if (ref?.current) {
        ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  };

  const handleGermanInvalidMessage = (e) => {
    const { validity, type } = e.target;

    if (validity.valueMissing) {
      e.target.setCustomValidity('Bitte füllen Sie dieses Feld aus.');
      return;
    }

    if (type === 'url' && validity.typeMismatch) {
      e.target.setCustomValidity('Bitte geben Sie eine gültige URL ein (z.B. https://beispiel.ch).');
      return;
    }

    e.target.setCustomValidity('');
  };

  const clearCustomValidityMessage = (e) => {
    e.target.setCustomValidity('');
  };
  
  // Setze timeDataSaved initial auf true wenn Aktivität geladen wird und Zeitdaten vorhanden sind
  const [timeDataSaved, setTimeDataSaved] = useState(() => {
    if (isEditMode && initialData) {
      const eventType = initialData.event_type;
      if (eventType === 'regular_hours') {
        return initialData.opening_hours && initialData.opening_hours.length > 0;
      } else {
        return initialData.event_schedule && Object.keys(initialData.event_schedule).length > 0;
      }
    }
    return false;
  });

  // Track form changes to warn before navigation
  useEffect(() => {
    if (isResettingFormRef.current) {
      isResettingFormRef.current = false;
      return;
    }
    setIsDirty(true);
  }, [formData]);

  // Block browser back button when form has unsaved changes
  useEffect(() => {
    if (!isDirty) return;
    // Push a sentinel state (same URL) so the first back press stays on this page
    window.history.pushState(null, '', window.location.href);

    const handlePopState = () => {
      if (window.confirm('Sie haben ungespeicherte Änderungen. Möchten Sie die Seite wirklich verlassen?')) {
        window.removeEventListener('popstate', handlePopState);
        setIsDirty(false);
        window.history.back();
      } else {
        window.history.pushState(null, '', window.location.href);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isDirty]);

  // Warn on page refresh / tab close
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    isResettingFormRef.current = true;
    const initial = mapActivityToFormData(initialData);
    setFormData(initial);
    const hasCoords = initial.latitude !== '' && initial.longitude !== '';
    const hasStructuredAddress = Boolean(
      (initial.locationStreet || '').trim() ||
      (initial.locationPlz || '').trim() ||
      (initial.locationCity || '').trim()
    );
    const hasLegacyAddress = (initial.locationAddress || '').trim() !== '' && !initial.locationAddress.startsWith('Koordinaten:');
    const hasRealAddress = hasStructuredAddress || hasLegacyAddress;
    setLocationInputMode(hasCoords && !hasRealAddress ? 'coordinates' : 'address');
    setIsDirty(false);
    
    // Aktualisiere timeDataSaved wenn initialData sich ändert
    if (isEditMode && initialData) {
      const eventType = initialData.event_type;
      if (eventType === 'regular_hours') {
        setTimeDataSaved(initialData.opening_hours && initialData.opening_hours.length > 0);
      } else {
        setTimeDataSaved(initialData.event_schedule && Object.keys(initialData.event_schedule).length > 0);
      }
    }
  }, [initialData, isEditMode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const composedAddress = composeAddress(formData);
    const hasPlzAndCity = (formData.locationPlz || '').trim() !== '' && (formData.locationCity || '').trim() !== '';
    const parsedCoords = locationInputMode === 'coordinates'
      ? parseCoordinates(formData.coordinateString)
      : null;
    const latitude = parsedCoords?.lat ?? parseFloat(formData.latitude);
    const longitude = parsedCoords?.lng ?? parseFloat(formData.longitude);
    const hasValidCoordinates = parsedCoords !== null;

    if (locationInputMode === 'address') {
      if (!hasPlzAndCity) {
        setError('Bitte geben Sie mindestens PLZ und Ort ein');
        scrollToRef(formRef);
        return;
      }
    }

    if (locationInputMode === 'coordinates') {
      if (!hasValidCoordinates) {
        setError('Koordinaten konnten nicht erkannt werden. Bitte im Format "47.4979, 8.7241" (Dezimal), "47°29\'52"N 8°43\'24"E" (DMS) oder "2699163, 1261335" (LV95) eingeben.');
        scrollToRef(formRef);
        return;
      }
    }

    // Validierung
    if (!isEditMode) {
      if (formData.categories.length === 0) {
        setError('Bitte wählen Sie mindestens eine Kategorie aus');
        scrollToRef(categoriesSectionRef);
        return;
      }
      
      // Prüfe, ob Zeitangaben validiert wurden (nur im Create-Modus)
      if (!timeDataSaved) {
        setError('Bitte validieren Sie zuerst die Zeitangaben mit dem "Validieren"-Button im Zeitangaben-Bereich');
        scrollToRef(eventScheduleSectionRef);
        return;
      }
    }

    // Spezielle Validierung für single_event
    if (formData.eventType === 'single_event') {
      if (formData.eventSchedule && formData.eventSchedule.dates) {
        const hasValidDate = formData.eventSchedule.dates.some(d => d.date && d.date.trim() !== '');
        if (!hasValidDate) {
          setError('Bitte geben Sie mindestens ein Datum für den Einzeltermin ein');
          scrollToRef(eventScheduleSectionRef);
          return;
        }
      } else {
        setError('Bitte geben Sie mindestens ein Datum für den Einzeltermin ein');
        scrollToRef(eventScheduleSectionRef);
        return;
      }
    }

    setLoading(true);
    
    try {
      // Verwende FormData für File-Upload
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('description', formData.description);

      if (locationInputMode === 'address') {
        formDataToSend.append('location_street', (formData.locationStreet || '').trim());
        formDataToSend.append('location_plz', (formData.locationPlz || '').trim());
        formDataToSend.append('location_city', (formData.locationCity || '').trim());
        formDataToSend.append('address', composedAddress);
      }
      if (locationInputMode === 'coordinates' && hasValidCoordinates) {
        formDataToSend.append('latitude', String(latitude));
        formDataToSend.append('longitude', String(longitude));
      }
      formDataToSend.append('location_name', formData.locationName);
      formDataToSend.append('age_min', formData.ageMin);
      formDataToSend.append('age_max', formData.ageMax);
      formDataToSend.append('indoor_outdoor', formData.weatherType);
      formDataToSend.append('cost_range', formData.cost);
      formDataToSend.append('duration', formData.duration);
      formDataToSend.append('website', formData.website);
      formDataToSend.append('location_website', formData.locationWebsite);
      formDataToSend.append('categories', JSON.stringify(formData.categories));
      formDataToSend.append('wheelchair_accessible', formData.wheelchairAccessible);
      formDataToSend.append('event_type', formData.eventType);
      formDataToSend.append('is_seasonal', formData.isSeasonal);
      if (formData.googlePlaceId) {
        formDataToSend.append('google_place_id', formData.googlePlaceId);
      }
      
      // Sende nur die relevanten Zeitdaten basierend auf event_type
      if (formData.eventType === 'regular_hours') {
        // Öffnungszeiten + optionale Gültigkeits-Datumsbereiche (z.B. Schulferien)
        formDataToSend.append('opening_hours', JSON.stringify(formData.openingHours));
        formDataToSend.append('event_schedule', JSON.stringify({
          date_ranges: formData.eventSchedule?.date_ranges || []
        }));
      } else if (formData.eventType === 'recurring_event') {
        // Nur event_schedule für wiederkehrende Events
        formDataToSend.append('opening_hours', JSON.stringify([]));
        formDataToSend.append('event_schedule', JSON.stringify(formData.eventSchedule));
      } else if (formData.eventType === 'single_event') {
        // Nur event_schedule für Einzeltermine
        formDataToSend.append('opening_hours', JSON.stringify([]));
        formDataToSend.append('event_schedule', JSON.stringify(formData.eventSchedule));
      } else if (formData.eventType === 'multi_day') {
        // Nur event_schedule für mehrtägige Events
        formDataToSend.append('opening_hours', JSON.stringify([]));
        formDataToSend.append('event_schedule', JSON.stringify(formData.eventSchedule));
      } else if (formData.eventType === 'freely_accessible') {
        // Nur event_schedule für frei zugängliche Events
        formDataToSend.append('opening_hours', JSON.stringify([]));
        formDataToSend.append('event_schedule', JSON.stringify(formData.eventSchedule));
        
        // Füge Zugänglichkeitsdaten hinzu (wenn vorhanden)
        if (formData.eventSchedule) {
          if (formData.eventSchedule.accessible_from) {
            formDataToSend.append('accessible_from', formData.eventSchedule.accessible_from);
          }
          if (formData.eventSchedule.accessible_until) {
            formDataToSend.append('accessible_until', formData.eventSchedule.accessible_until);
          }
        }
      }
      
      if (isEditMode) {
        const response = await activitiesAPI.update(activityId, formDataToSend);
        setIsDirty(false);
        onSuccess?.(response?.data);
      } else {
        const response = await activitiesAPI.create(formDataToSend);
        setIsDirty(false);
        onSuccess?.(response?.data);
      }
    } catch (error) {
      console.error('Error saving activity:', error);
      const errorMsg = error.response?.data?.error || (isEditMode
        ? 'Fehler beim Aktualisieren der Aktivität'
        : 'Fehler beim Erstellen der Aktivität');
      setError(errorMsg);
      scrollToRef(formRef);
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (category) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category]
    }));
  };

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="activity-form">
      {error && <div className="error-message">{error}</div>}
      
      {/* Grundinformationen */}
      <div className="form-section">
        <h2 className="text-xl mb-4 pb-2 border-b">Grundinformationen</h2>
        
        <div className="space-y-4">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-1)' }}>
              <Label htmlFor="name">Name der Aktivität</Label>
              <FieldInfo text={info.name} />
            </div>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="z.B. Tierpark Goldau"
              required
              onInvalid={handleGermanInvalidMessage}
              onInput={clearCustomValidityMessage}
            />
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-1)' }}>
              <Label htmlFor="description">Beschreibung</Label>
              <FieldInfo text={info.description} />
            </div>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Beschreiben Sie die Aktivität..."
              className="input"
              rows="4"
              required
              onInvalid={handleGermanInvalidMessage}
              onInput={clearCustomValidityMessage}
            />
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-1)' }}>
              <Label htmlFor="website">Webseite der Aktivität (optional)</Label>
              <FieldInfo text={info.website} />
            </div>
            <Input
              id="website"
              type="url"
              value={formData.website}
              onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
              placeholder="https://..."
              onInvalid={handleGermanInvalidMessage}
              onInput={clearCustomValidityMessage}
            />
            <p className="text-sm text-gray-500 mt-1">
              Link zur Beschreibung der Aktivität
            </p>
          </div>
        </div>
      </div>

      {/* Standort */}
      <div className="form-section">
        <h2 className="text-xl mb-4 pb-2 border-b">Standort</h2>
        
        <div className="space-y-4">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-1)' }}>
              <Label htmlFor="locationName">Ortsbezeichnung</Label>
              <FieldInfo text={info.locationName} />
            </div>
            <Input
              id="locationName"
              value={formData.locationName}
              onChange={(e) => setFormData(prev => ({ ...prev, locationName: e.target.value }))}
              placeholder="z.B. Zentrum Paul Klee"
              required
              onInvalid={handleGermanInvalidMessage}
              onInput={clearCustomValidityMessage}
            />
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <Label>Standort-Eingabe</Label>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant={locationInputMode === 'address' ? 'default' : 'outline'}
                onClick={() => {
                  setLocationInputMode('address');
                  setFormData(prev => ({ ...prev, latitude: '', longitude: '' }));
                }}
                className="rounded-full"
              >
                Adresse
              </Button>
              <Button
                type="button"
                variant={locationInputMode === 'coordinates' ? 'default' : 'outline'}
                onClick={() => {
                  setLocationInputMode('coordinates');
                  setFormData(prev => ({
                    ...prev,
                    locationStreet: '',
                    locationPlz: '',
                    locationCity: '',
                    locationAddress: '',
                  }));
                }}
                className="rounded-full"
              >
                Koordinaten
              </Button>
            </div>
          </div>

          {locationInputMode === 'address' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-1)' }}>
              <Label>Adresse</Label>
              <FieldInfo text={info.locationAddress} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div>
                <Label htmlFor="locationStreet">Strasse</Label>
                <Input
                  id="locationStreet"
                  value={formData.locationStreet}
                  onChange={(e) => setFormData(prev => ({ ...prev, locationStreet: e.target.value }))}
                  placeholder="Strasse"
                  onInvalid={handleGermanInvalidMessage}
                  onInput={clearCustomValidityMessage}
                />
              </div>
              <div>
                <Label htmlFor="locationPlz">PLZ</Label>
                <Input
                  id="locationPlz"
                  value={formData.locationPlz}
                  onChange={(e) => setFormData(prev => ({ ...prev, locationPlz: e.target.value }))}
                  placeholder="PLZ"
                  required={locationInputMode === 'address'}
                  onInvalid={handleGermanInvalidMessage}
                  onInput={clearCustomValidityMessage}
                />
              </div>
              <div>
                <Label htmlFor="locationCity">Ort</Label>
                <Input
                  id="locationCity"
                  value={formData.locationCity}
                  onChange={(e) => setFormData(prev => ({ ...prev, locationCity: e.target.value }))}
                  placeholder="Ort"
                  required={locationInputMode === 'address'}
                  onInvalid={handleGermanInvalidMessage}
                  onInput={clearCustomValidityMessage}
                />
              </div>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              Aus den Feldern wird automatisch gespeichert als: "Strasse, PLZ Ort". Strasse darf leer bleiben.
            </p>
          </div>
          )}

          {locationInputMode === 'coordinates' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-1)' }}>
              <Label htmlFor="coordinateString">Koordinaten *</Label>
            </div>
            <Input
              id="coordinateString"
              type="text"
              value={coordinateInput}
              onChange={(e) => {
                const val = e.target.value;
                const parsed = parseCoordinates(val);
                setFormData(prev => ({
                  ...prev,
                  coordinateString: val,
                  latitude: parsed ? String(parsed.lat) : '',
                  longitude: parsed ? String(parsed.lng) : '',
                }));
              }}
              placeholder={'z.B. 47.4979, 8.7241  |  47°29\'52"N 8°43\'24"E  |  2699163, 1261335'}
              required={locationInputMode === 'coordinates'}
            />
            {coordinateInput.trim() !== '' && (() => {
              const parsed = parseCoordinates(coordinateInput);
              if (parsed) {
                return (
                  <p className="text-sm text-green-600 mt-1">
                    Erkannt: {formatLabel(parsed.format)} → {parsed.lat}, {parsed.lng}
                  </p>
                );
              }
              return (
                <p className="text-sm text-red-500 mt-1">
                  Format nicht erkannt. Bitte WGS84 Dezimal, DMS oder LV95 eingeben.
                </p>
              );
            })()}
            {!coordinateInput.trim() && (
              <p className="text-sm text-gray-500 mt-1">
                Unterstützte Formate: WGS84 Dezimal, WGS84 Grad/Min/Sek, LV95
              </p>
            )}
          </div>
          )}

          {/* Event Schedule Manager */}
          <div ref={eventScheduleSectionRef}>
            <EventScheduleManager
            eventType={formData.eventType}
            openingHours={formData.openingHours}
            eventSchedule={formData.eventSchedule}
            isSeasonal={formData.isSeasonal}
            website={formData.locationWebsite}
            isEditMode={isEditMode}
            onWebsiteChange={(locationWebsite) => setFormData(prev => ({ ...prev, locationWebsite }))}
            onEventTypeChange={(type) => {
              setFormData(prev => ({ ...prev, eventType: type }));
              setTimeDataSaved(false);
            }}
            onOpeningHoursChange={(hours) => setFormData(prev => ({ ...prev, openingHours: hours }))}
            onEventScheduleChange={(schedule) => setFormData(prev => ({ ...prev, eventSchedule: schedule }))}
            onSeasonalChange={(isSeasonal) => setFormData(prev => ({ ...prev, isSeasonal }))}
            onSaveStatusChange={(saved) => setTimeDataSaved(saved)}
            onImportFromWebsite={async () => {
              if (!formData.locationWebsite) {
                throw new Error('Bitte geben Sie zuerst die Webseite der Location ein');
              }
              let locationWebsite = formData.locationWebsite.trim();
              if (!locationWebsite.startsWith('http://') && !locationWebsite.startsWith('https://')) {
                locationWebsite = 'https://' + locationWebsite;
                setFormData(prev => ({ ...prev, locationWebsite }));
              }
              const result = await activitiesAPI.importOpeningHoursFromWebsite(
                formData.locationName,
                composeAddress(formData),
                locationWebsite
              );
              // Update formData even if opening_hours is empty/null
              setFormData(prev => ({
                ...prev, 
                openingHours: result.data.opening_hours || [],
                googlePlaceId: result.data.google_place_id 
              }));
              
              // Still return the data for the component to show messages
              return result.data;
            }}
            onImportOpeningHours={async () => {
              if (!activityId) {
                throw new Error('Aktivität muss zuerst gespeichert werden');
              }
              const result = await activitiesAPI.importOpeningHours(activityId);
              if (result.data.opening_hours) {
                setFormData(prev => ({ 
                  ...prev, 
                  openingHours: result.data.opening_hours,
                  googlePlaceId: result.data.google_place_id || prev.googlePlaceId
                }));
              }
              return result.data;
            }}
            address={composeAddress(formData)}
            canImportFromGoogle={!!activityId}
            />
          </div>
        </div>
      </div>

      {/* Kategorisierung */}
      <div ref={categoriesSectionRef} className="form-section">
        <h2 className="text-xl mb-4 pb-2 border-b">Kategorisierung</h2>
        
        <div className="space-y-4">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-1)', marginBottom: 'var(--spacing-2)' }}>
              <Label>Kategorien * (mindestens eine auswählen)</Label>
              <FieldInfo text={info.categories} />
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <Button
                  key={category}
                  type="button"
                  variant={formData.categories.includes(category) ? 'default' : 'outline'}
                  onClick={() => toggleCategory(category)}
                  className="rounded-full"
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-1)' }}>
              <Label>Altersbereich * ({formData.ageMin}–{formData.ageMax} Jahre)</Label>
              <FieldInfo text={info.ageRange} />
            </div>
            <div className="mt-4">
              <Slider
                value={[formData.ageMin, formData.ageMax]}
                onValueChange={([min, max]) => setFormData(prev => ({ ...prev, ageMin: min, ageMax: max }))}
                min={0}
                max={12}
                step={1}
                className="w-full"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Eigenschaften */}
      <div className="form-section">
        <h2 className="text-xl mb-4 pb-2 border-b">Eigenschaften</h2>
        
        <div className="space-y-4">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-1)' }}>
              <Label htmlFor="weatherType">Wetterabhängigkeit *</Label>
              <FieldInfo text={info.weatherType} />
            </div>
            <Select
              value={formData.weatherType}
              onValueChange={(value) => setFormData(prev => ({ ...prev, weatherType: value }))}
              required
            >
              <SelectTrigger>
                <SelectValue placeholder="Wählen Sie eine Option" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="indoor">Indoor</SelectItem>
                <SelectItem value="outdoor">Outdoor</SelectItem>
                <SelectItem value="both">Wetterunabhängig</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-1)' }}>
              <Label htmlFor="cost">Kosten *</Label>
              <FieldInfo text={info.cost} />
            </div>
            <Select
              value={formData.cost}
              onValueChange={(value) => setFormData(prev => ({ ...prev, cost: value }))}
              required
            >
              <SelectTrigger>
                <SelectValue placeholder="Wählen Sie eine Option" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="free">Gratis</SelectItem>
                <SelectItem value="low">0-10 CHF</SelectItem>
                <SelectItem value="medium">10-30 CHF</SelectItem>
                <SelectItem value="high">30+ CHF</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-1)' }}>
              <Label htmlFor="duration">Dauer *</Label>
              <FieldInfo text={info.duration} />
            </div>
            <Select
              value={formData.duration}
              onValueChange={(value) => setFormData(prev => ({ ...prev, duration: value }))}
              required
            >
              <SelectTrigger>
                <SelectValue placeholder="Wählen Sie eine Option" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="short">Weniger als 1 Stunde</SelectItem>
                <SelectItem value="medium">1-3 Stunden</SelectItem>
                <SelectItem value="halfday">Halber Tag</SelectItem>
                <SelectItem value="fullday">Ganzer Tag</SelectItem>
                <SelectItem value="multiday">Mehrtägig</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center">
            <Checkbox
              id="wheelchairAccessible"
              checked={formData.wheelchairAccessible}
              onCheckedChange={(checked) => 
                setFormData(prev => ({ ...prev, wheelchairAccessible: !!checked }))
              }
            />
            <Label htmlFor="wheelchairAccessible" className="ml-2 cursor-pointer">
              Rollstuhlgerecht
            </Label>
            <FieldInfo text={info.wheelchairAccessible} />
          </div>
        </div>
      </div>

      <div className="form-actions">
        <button type="submit" disabled={loading || (!isEditMode && !timeDataSaved)} className="submit-btn">
          {loading ? (isEditMode ? 'Wird gespeichert...' : 'Wird erstellt...') : (isEditMode ? 'Aktivität speichern' : 'Aktivität erstellen')}
        </button>
        {onCancel && (
          <button type="button" className="cancel-btn" onClick={onCancel}>
            Abbrechen
          </button>
        )}
      </div>
    </form>
  );
};

export default ActivityForm;
